import { nextTick, onUnmounted, ref, watch } from 'vue'

import { onViewportChange, viewportState, visibleBox } from '../utils/viewport'

/**
 * La lista de sugerencias colgada de un campo en el que se escribe.
 *
 * En un teléfono un autocompletado tiene a la vez teclado y desplegable, y cada
 * uno se comía al otro: la lista se abría debajo de las teclas, el campo se
 * quedaba a media pantalla sin sitio para nada debajo, al tocar una fila el
 * mismo toque acababa en lo que había detrás de la lista, y cada pantalla con
 * uno de estos lo resolvía (o no) a su manera. Aquí está la manera, una vez:
 *
 *   - La lista va teleportada a <body> y se coloca contra el campo DENTRO de lo
 *     que se ve (`visibleBox()`), debajo si cabe y encima si no, con el alto
 *     que de verdad queda. La sigue al hacer scroll y al salir o entrar el
 *     teclado, una vez por fotograma como mucho.
 *   - Con el teclado fuera, el campo sube a lo alto de lo visible —haciendo
 *     scroll de su contenedor, no de la ventana— para dejar sitio a la lista.
 *   - Tocar fuera la cierra (con el `mousedown`, que en un táctil solo llega
 *     con un toque y no con un arrastre: hacer scroll de la página no cierra);
 *     pasar el foco a otro campo también.
 *
 * Lo que NO hace, y le toca a la plantilla de quien lo usa, porque son dos
 * atributos y esconderlos detrás de una función los haría más fáciles de
 * olvidar: cada fila lleva `@mousedown.prevent` (el campo no pierde el foco y
 * el teclado no se cierra) y elige en `@click`, nunca en el mousedown. Elegir
 * en el mousedown cerraba la lista antes de que llegara el click del mismo
 * toque, y ese click caía en lo que había debajo: el «toque fantasma». Y un
 * click no llega después de un arrastre, así que hacer scroll por la lista con
 * el dedo no elige nada.
 *
 * Uso:
 *
 *   const { style, reveal } = useSuggestionPopover({
 *     anchor: root, list, open, onDismiss: close, maxHeight: 260,
 *   })
 *   // <input @focus="reveal()"> y en <Teleport to="body">
 *   // <ul v-if="open" ref="list" class="… suggestion-list" :style="style">
 *
 * En el escritorio todo queda como estaba: la colocación es la misma cuenta y
 * lo del teléfono (subir el campo, filas de 44 px) solo actúa con un dedo.
 */

/** Un dedo y no un ratón: lo mismo que pregunta el resto del ui-kit. */
const TOUCH_QUERY = '(pointer: coarse)'

/** Lo que se deja libre contra el borde de lo visible. */
const EDGE = 8

/** Dónde se queda el campo cuando se sube: casi arriba, no pegado. */
const TOP_ROOM = 12

/**
 * Lo que tarda el teclado en acabar de salir. Durante la animación llegan
 * varios avisos del viewport; colocar el campo con el primero lo dejaba donde
 * NO iba a estar y había que hacerlo otra vez, que es el salto que se ve.
 */
const SETTLE_MS = 160

/** Menos que esto es la barra del navegador, no el teclado. */
const KEYBOARD_STEP = 100

export function isTouchScreen() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(TOUCH_QUERY).matches
}

/**
 * El primer antepasado que de verdad hace scroll.
 *
 * Mover la ventana no sirve: el armazón de la app mide lo que se ve y es una
 * caja de dentro (el contenido de la página, el cuerpo de un diálogo) la que se
 * desplaza. Solo si no hay ninguna se cae a la del documento.
 */
function scrollerOf(element) {
  for (let node = element?.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    const scrolls = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    if (scrolls && node.scrollHeight > node.clientHeight + 1) return node
  }
  return document.scrollingElement ?? document.documentElement
}

/**
 * Sube el campo a lo alto de lo visible si debajo no queda `needBelow` libre.
 *
 * Solo en un táctil: en el escritorio un campo no se mueve porque lo enfoques.
 * Si debajo ya cabe lo que hace falta no hace nada, así que llamarlo dos veces
 * no mueve la página dos veces. Devuelve si movió algo.
 */
export function revealField(element, needBelow = 0) {
  if (!element || !isTouchScreen()) return false

  const rect = element.getBoundingClientRect()
  const box = visibleBox()
  const scroller = scrollerOf(element)
  // Lo alto de lo visible es lo alto de la caja que hace scroll, no el de la
  // ventana: por encima quedan la cabecera de la app o la de un diálogo, y
  // subir el campo hasta el borde de la ventana lo escondía debajo de ellas.
  const isPage = scroller === document.scrollingElement || scroller === document.documentElement
  const top = isPage ? box.top : Math.max(box.top, scroller.getBoundingClientRect().top)
  const below = box.bottom - rect.bottom - EDGE
  if (rect.top >= top + EDGE && below >= needBelow) return false

  const delta = Math.round(rect.top - (top + TOP_ROOM))
  if (Math.abs(delta) < 4) return false

  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  scroller.scrollBy({ top: delta, behavior: calm ? 'auto' : 'smooth' })
  return true
}

/**
 * `revealField` en el momento en que sirve: al enfocar, y otra vez cuando el
 * teclado termina de salir con el campo aún enfocado.
 *
 * Al enfocar el teclado todavía no está —el foco llega antes— y lo visible es
 * la pantalla entera: casi siempre cabe todo y no se mueve nada. Lo que cuenta
 * es el aviso de después, cuando lo visible encoge; por eso se escucha el
 * viewport y se espera a que deje de cambiar.
 *
 * @param {import('vue').Ref<HTMLElement|null>} anchor  el campo (o su marco)
 * @param {() => number} needBelow  cuánto hace falta libre debajo, en px
 */
export function useRevealOnKeyboard(anchor, needBelow = () => 0) {
  let timer = null
  // El alto de lo visible antes de que saliera el teclado.
  let baseline = 0

  const focused = () => {
    const element = anchor.value
    return Boolean(element && typeof document !== 'undefined' && element.contains(document.activeElement))
  }
  const run = () => { if (focused()) revealField(anchor.value, needBelow()) }

  const stop = onViewportChange((state) => {
    if (state.height >= baseline) { baseline = state.height; return }
    if (baseline - state.height < KEYBOARD_STEP || !focused()) return
    clearTimeout(timer)
    timer = setTimeout(() => {
      // El teclado ya está: lo que se ve ahora es la nueva referencia, para no
      // volver a subir el campo con cada scroll del teclado abierto.
      baseline = viewportState().height
      run()
    }, SETTLE_MS)
  })

  onUnmounted(() => { stop(); clearTimeout(timer) })

  return {
    /** Para el `@focus` del campo. */
    reveal: () => { clearTimeout(timer); timer = setTimeout(run, 0) },
  }
}

/**
 * @param {object} options
 * @param {import('vue').Ref<HTMLElement|null>} options.anchor  contra qué se coloca
 * @param {import('vue').Ref<HTMLElement|null>} options.list    la lista teleportada
 * @param {import('vue').Ref<boolean>} options.open             si se ve
 * @param {() => void} [options.onDismiss]  tocar fuera o irse a otro campo
 * @param {number} [options.maxHeight]      el tope de la lista
 * @param {number} [options.gap]            la separación con el campo
 * @returns {{ style: import('vue').Ref<object>, place: () => void, reveal: () => void }}
 */
export function useSuggestionPopover({ anchor, list, open, onDismiss = null, maxHeight = 260, gap = 4 } = {}) {
  const style = ref({})
  let frame = 0
  let observer = null

  /** Lo que mediría la lista sin tope: con `max-height` puesto sigue siendo esto. */
  const wanted = () => Math.min(maxHeight, list.value?.scrollHeight || maxHeight)

  function place() {
    const element = anchor.value
    if (!element) return
    const rect = element.getBoundingClientRect()
    const box = visibleBox()

    // El campo se ha ido de lo visible (el scroll se lo llevó): una lista
    // flotando sola en mitad de la pantalla no es de nadie.
    if (rect.bottom < box.top || rect.top > box.bottom) {
      style.value = { ...style.value, visibility: 'hidden' }
      return
    }

    const height = wanted()
    const below = box.bottom - rect.bottom - gap - EDGE
    const above = rect.top - box.top - gap - EDGE
    const up = height > below && above > below
    // En el escritorio, el suelo de siempre (120). Con el teclado fuera ese
    // suelo empujaba la lista debajo de las teclas: ahí manda el hueco real,
    // con una fila como mínimo.
    const floor = isTouchScreen() ? 44 : 120
    const room = Math.max(floor, Math.min(maxHeight, Math.floor(up ? above : below)))
    const shown = Math.min(height, room)

    style.value = {
      position: 'fixed',
      left: `${Math.round(rect.left)}px`,
      width: `${Math.round(rect.width)}px`,
      top: `${Math.round(up ? rect.top - gap - shown : rect.bottom + gap)}px`,
      maxHeight: `${room}px`,
    }
  }

  // Una vez por fotograma como mucho: el scroll y el teclado avisan muchas
  // veces por segundo, y recolocar en cada aviso es la lista que tiembla.
  // El scroll de la PROPIA lista no la mueve: con el dedo dentro, recolocarla
  // era moverla debajo del dedo.
  function schedule(event) {
    // El `resize` llega con la ventana de destino, que no es un nodo.
    const target = event?.target
    if (typeof Node !== 'undefined' && target instanceof Node && list.value?.contains(target)) return
    if (frame) return
    frame = requestAnimationFrame(() => { frame = 0; place() })
  }

  const inside = (target) => Boolean(anchor.value?.contains(target) || list.value?.contains(target))
  const onPress = (event) => { if (!inside(event.target)) onDismiss?.() }
  const onFocusIn = (event) => { if (!inside(event.target)) onDismiss?.() }

  let stopViewport = null
  function listen(on) {
    const method = on ? 'addEventListener' : 'removeEventListener'
    // En captura: el scroll no sube, y así cuenta el de una tarjeta o un diálogo.
    window[method]('scroll', schedule, true)
    window[method]('resize', schedule)
    document[method]('mousedown', onPress, true)
    document[method]('focusin', onFocusIn)
    if (on) {
      stopViewport = onViewportChange(() => schedule())
    } else {
      stopViewport?.()
      stopViewport = null
    }
  }

  watch(open, async (isOpen) => {
    listen(false)
    observer?.disconnect()
    observer = null
    if (!isOpen) return
    place()
    await nextTick()
    place()
    listen(true)
    // La lista abre con una fila («buscando…») y luego crece a ocho: abierta
    // hacia arriba, sin esto se quedaba colgando por debajo del campo.
    if (list.value && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => schedule())
      observer.observe(list.value)
    }
  }, { immediate: true })

  const { reveal } = useRevealOnKeyboard(anchor, () => wanted() + gap + EDGE)

  onUnmounted(() => {
    listen(false)
    observer?.disconnect()
    if (frame) cancelAnimationFrame(frame)
  })

  return { style, place, reveal }
}
